import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Clock, Target } from 'lucide-react';
import type { GoalStatus } from '@pms/shared';
import { goalsService } from '@/services/goals.service';
import { SearchInput } from '@/components/ui/search-input';
import { StatCard } from '@/components/ui/stat-card';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { GoalProgressRing } from '@/components/goals/GoalProgressBar';
import { ALL_GOAL_STATUSES, ALL_OPTION, GOAL_STATUS_META } from '@/components/goals/goalConstants';
import { GoalCard } from './GoalCard';

interface Props {
  onOpen: (id: string) => void;
}

const SEARCH_DEBOUNCE_MS = 350;
const PAGE_SIZE = 24;

export function MyGoalsTab({ onOpen }: Props) {
  const queryClient = useQueryClient();
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { setDebouncedQ(q); setPage(1); }, SEARCH_DEBOUNCE_MS);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [q]);

  const summaryQuery = useQuery({
    queryKey: ['goals', 'summary', 'mine'],
    queryFn: () => goalsService.summary(true),
  });

  const params = {
    mine: true,
    page,
    limit: PAGE_SIZE,
    q: debouncedQ || undefined,
    status: (status || undefined) as GoalStatus | undefined,
    sort: 'due_date:asc',
  };

  const listQuery = useQuery({
    queryKey: ['goals', 'list', params],
    queryFn: () => goalsService.listGoals(params),
  });

  const goals = listQuery.data?.data ?? [];
  const summary = summaryQuery.data;
  const pagination = listQuery.data?.pagination;

  return (
    <div className="space-y-4">
      {/* Aggregate band */}
      <div className="flex flex-col gap-4 rounded-xl bg-gradient-to-br from-primary to-teal-700 p-4 text-white shadow-card sm:flex-row sm:items-center sm:gap-6 sm:p-5">
        <GoalProgressRing
          percent={summary?.avg_progress ?? 0}
          label="Avg progress"
          sub={summary ? `${summary.total} goal${summary.total === 1 ? '' : 's'}` : undefined}
        />
        <div className="min-w-0 flex-1">
          <h2 className="text-lg font-semibold tracking-tight">Your goals at a glance</h2>
          <p className="mt-1 text-sm text-white/75">
            {summary && summary.total === 0
              ? 'No goals yet — create one to start tracking a measurable target.'
              : summary
                ? `${summary.by_status.active} active · ${summary.by_status.achieved} achieved · ${summary.due_this_month} due this month`
                : 'Loading your summary…'}
          </p>
          {summary && (summary.overdue > 0 || summary.at_risk > 0) && (
            <p className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-white/15 px-2.5 py-1 text-xs font-medium">
              <AlertTriangle className="h-3.5 w-3.5" />
              {summary.overdue > 0 && `${summary.overdue} overdue`}
              {summary.overdue > 0 && summary.at_risk > 0 && ' · '}
              {summary.at_risk > 0 && `${summary.at_risk} at risk`}
            </p>
          )}
        </div>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Active" value={summary?.by_status.active ?? '—'} icon={Target} tone="teal" hint="Currently being worked on" />
        <StatCard label="Achieved" value={summary?.by_status.achieved ?? '—'} icon={CheckCircle2} tone="emerald" hint="Approved as complete" />
        <StatCard label="At Risk" value={summary?.at_risk ?? '—'} icon={Clock} tone="amber" hint="Behind the expected pace" />
        <StatCard label="Overdue" value={summary?.overdue ?? '—'} icon={AlertTriangle} tone="rose" hint="Past the deadline" />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <SearchInput
          value={q}
          onChange={setQ}
          placeholder="Search your goals…"
          containerClassName="min-w-[200px] flex-1 max-w-xs"
        />
        <Select
          value={status || ALL_OPTION}
          onValueChange={(v) => { setStatus(v === ALL_OPTION ? '' : v); setPage(1); }}
        >
          <SelectTrigger className="h-10 w-44">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_OPTION}>All statuses</SelectItem>
            {ALL_GOAL_STATUSES.map((s) => (
              <SelectItem key={s} value={s}>{GOAL_STATUS_META[s].label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {pagination && (
          <span className="ml-auto text-xs text-muted-foreground">
            {pagination.total} goal{pagination.total === 1 ? '' : 's'}
          </span>
        )}
      </div>

      {/* Card grid with the four required states */}
      {listQuery.isLoading ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-44 animate-pulse rounded-xl bg-muted/50" />
          ))}
        </div>
      ) : listQuery.isError ? (
        <div className="flex flex-col items-center gap-3 py-16 text-center text-destructive">
          <AlertTriangle className="h-8 w-8 opacity-60" />
          <p className="text-sm font-medium">Failed to load your goals</p>
          <Button variant="outline" size="sm" onClick={() => void listQuery.refetch()}>Retry</Button>
        </div>
      ) : goals.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-16 text-center">
          <Target className="h-10 w-10 text-muted-foreground/50" />
          <p className="text-sm text-muted-foreground">
            {debouncedQ || status
              ? 'No goals match the current filters'
              : 'No goals yet — use “New Goal” to set your first measurable target'}
          </p>
        </div>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {goals.map((goal) => (
              <GoalCard key={goal.id} goal={goal} onOpen={() => onOpen(goal.id)} />
            ))}
          </div>
          {pagination && pagination.totalPages > 1 && (
            <div className="flex items-center justify-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                Previous
              </Button>
              <span className="text-xs text-muted-foreground">
                Page {pagination.page} of {pagination.totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= pagination.totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          )}
        </>
      )}

      {/* Retry hook for the summary band, kept out of the way when healthy */}
      {summaryQuery.isError && (
        <button
          type="button"
          onClick={() => void queryClient.invalidateQueries({ queryKey: ['goals', 'summary'] })}
          className="text-xs text-destructive underline"
        >
          Summary failed to load — retry
        </button>
      )}
    </div>
  );
}
